/**
 * RevShare API Service
 * Endpoints for managing RevShare offers
 */

import ApiClient from '../apiClient';
import { PaginatedResponse, TrafficSource } from './types';

// ============ Types ============

export type OfferStatus = 'moderation' | 'active' | 'hidden' | 'refusal';

export type OfferType = 'free' | 'free trial';

export type OwnerType = 'creator' | 'agency';

export type OwnerLevel = 'new' | 'base' | 'pro' | 'expert' | 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

export interface RefShareOfferOwner {
  _id?: string;
  id?: string;
  name: string;
  avatar?: string;
  type?: OwnerType;
  lvl?: OwnerLevel;
}

export interface RefShareOfferData {
  commission: number;
  prohibited_sources?: TrafficSource[];
  show_service_info_for_partner?: boolean;
  custom_media_for_partner?: boolean;
  exist_chatters?: boolean;
  chat_processing?: 'all time' | 'custom work time' | 'AI' | 'not specified';
  free_trial_access?: boolean;
  description?: string;
  status: OfferStatus;
}

export interface RefShareOfferOfData {
  subscribers?: number;
  overall_popularity_percent?: number;
  favorited?: number;
  posts?: number;
  balance?: number;
  blocked_countries?: string[];
  arpu?: number;
  conversion?: number;
  last_update?: string;
}

export interface RefShareOffer {
  _id: string;
  id?: string;
  of_user_id: number;
  of_user_name: string;
  name: string;
  avatar?: string;
  type?: OfferType;
  status?: OfferStatus;
  created_at?: string;
  of_data?: RefShareOfferOfData;
  owner?: RefShareOfferOwner | string;
  offer?: RefShareOfferData;
  track_link?: {
    _id: string;
    link: string;
    track_link_id: number;
  };
  public_content?: string[];
  ref_share_active_by_source?: Record<string, number>;
  ref_share_active_total?: number;
  ref_share_total_count?: number;
  ref_share_subscribed_total?: number;
  ref_share_transactions_per_subscriber?: number;
  ref_share_with_gte_3_chat_messages_total?: number;
  ref_share_transactions_total?: number | string;
  sfs_posts_level?: 'High' | 'Medium' | 'Low' | 'None';
  granted?: {
    need: number;
    dep?: number | null;
  };
}

// ============ Request Types ============

export type RefShareOffersSortBy =
  | 'created_at'
  | '-created_at'
  | 'offer.commission'
  | '-offer.commission'
  | 'of_data.subscribers'
  | '-of_data.subscribers'
  | 'of_data.favorited'
  | '-of_data.favorited'
  | 'of_data.overall_popularity_percent'
  | '-of_data.overall_popularity_percent'
  | 'ref_share_with_gte_3_chat_messages_total'
  | '-ref_share_with_gte_3_chat_messages_total'
  | 'ref_share_transactions_total'
  | '-ref_share_transactions_total';

export interface GetRefShareOffersParams {
  page?: number;
  page_size?: number;
  sort_by?: RefShareOffersSortBy;
  search?: string;
  type?: OfferType;
  performer_top?: number;
  commission?: number;
  guaranteed?: boolean;
  available_source_in?: TrafficSource;
  available_source_out?: TrafficSource;
  owner?: string;
  owner_type?: OwnerType;
  status?: OfferStatus;
}

export interface UpdateRefShareOfferParams {
  status?: OfferStatus;
  commission?: number;
  prohibited_sources?: TrafficSource[];
}

// ============ Order (Campaign) Types ============

export type OrderStatus = 'in_progress' | 'in_moderation' | 'waiting' | 'canceled' | 'completed';

export interface RefShareOrderModel {
  _id?: string;
  id?: string;
  of_user_id: number;
  of_user_name: string;
  name?: string;
  avatar?: string;
  link?: string;
  track_link_id?: number;
  transitions_count?: number;
  overall_popularity_percent?: number;
  blocked_countries?: string[];
  of_data?: {
    subscribers?: number;
    favorited?: number;
    posts?: number;
  };
}

export interface RefShareOrderOffer {
  commission: number;
  prohibited_sources?: TrafficSource[];
  free_trial_access?: boolean;
  status?: string;
  client?: {
    _id?: string;
    id?: string;
    name: string;
    avatar?: string;
    lvl?: OwnerLevel;
    type?: OwnerType;
  };
}

export interface RefShareOrderOwner {
  _id?: string;
  id?: string;
  name: string;
  avatar?: string;
  lvl?: OwnerLevel;
  type?: OwnerType;
}

export interface RefShareOrderPartner {
  _id?: string;
  id?: string;
  name: string;
  avatar?: string;
  lvl?: OwnerLevel;
}

export interface TransactionStatistic {
  total?: number | string;
  count?: number;
}

export interface RefShareOrder {
  _id: string;
  id?: string;
  model: RefShareOrderModel;
  owner: RefShareOrderOwner;
  owner_type?: OwnerType;
  partner?: RefShareOrderPartner;
  type?: 'free' | 'free trial';
  source?: TrafficSource;
  status: OrderStatus;
  created_at?: string;
  last_update?: string;
  offer?: RefShareOrderOffer;
  subscribed_count?: number;
  transactions_statistic?: TransactionStatistic;
  subs?: {
    count?: number;
    with_gte_3_chat_messages_count?: number;
  };
  name?: string;
  description?: string;
  public_content?: string[];
}

export interface GetRefShareOrdersParams {
  page?: number;
  page_size?: number;
  id?: string;
  source?: TrafficSource;
  status?: OrderStatus;
  model?: number;
  type?: OwnerType;
  search?: string;
  owner?: string;
  client?: string;
  ordering?: string;
}

export interface AvailableModel {
  _id: string;
  of_user_id: number;
  of_user_name: string;
  avatar?: string;
}

// ============ Response Types ============

export type RefShareOffersResponse = PaginatedResponse<RefShareOffer>;
export type RefShareOrdersResponse = PaginatedResponse<RefShareOrder> & {
  transactions_total_5_percent?: string;
};
export type AvailableModelsResponse = PaginatedResponse<AvailableModel>;

// ============ Cost (Bill) Types ============

export type CostStatus = 'in_pending' | 'paid';

export interface CostClient {
  _id: string;
  name?: string;
  avatar?: string;
  lvl?: OwnerLevel;
  type?: OwnerType;
}

export interface CostPartner {
  _id: string;
  name?: string;
  avatar?: string;
  lvl?: OwnerLevel;
}

export interface CostModel {
  _id: string;
  name?: string;
  avatar?: string;
  of_user_name?: string;
  overall_popularity_percent?: number;
}

export interface RefShareCost {
  _id: string;
  amount: string | number;
  status: CostStatus;
  created_at: string;
  description?: string | null;
  company_id: number;
  inner_company_id?: string;
  client: CostClient;
  partner: CostPartner;
  model: CostModel;
}

export interface GetRefShareCostsParams {
  page?: number;
  page_size?: number;
  search?: string;
  amount?: number;
  client_id?: string;
  company_id?: number;
  inner_company_id?: string;
  partner_id?: string;
  status?: CostStatus;
  created_at__start?: string;
  created_at__end?: string;
  model_id?: string;
  sort_by?: 'created_at' | '-created_at';
}

export type RefShareCostsResponse = PaginatedResponse<RefShareCost>;

// ============ API Service ============

export const refShareApi = {
  /**
   * Get list of RevShare offers
   */
  getOffers: async (params?: GetRefShareOffersParams): Promise<RefShareOffersResponse> => {
    const response = await ApiClient.get('/staff/ref-share/offers', { params });
    return response.data;
  },

  /**
   * Change offer status (activate/deactivate)
   */
  updateOfferStatus: async (modelId: string, status: OfferStatus): Promise<{ status: OfferStatus }> => {
    const response = await ApiClient.post(`/staff/ref-share/offers/${modelId}/change_offer_status`, {
      status
    });
    return response.data;
  },

  /**
   * Update offer
   */
  updateOffer: async (modelId: string, data: UpdateRefShareOfferParams): Promise<RefShareOffer> => {
    const response = await ApiClient.patch(`/staff/ref-share/offers/${modelId}`, data);
    return response.data;
  },

  // ============ Orders (Campaigns) ============

  /**
   * Get list of RevShare orders (campaigns)
   */
  getOrders: async (params?: GetRefShareOrdersParams): Promise<RefShareOrdersResponse> => {
    const response = await ApiClient.get('/staff/ref-share/orders', { params });
    return response.data;
  },

  /**
   * Change order (campaign) status
   */
  changeOrderStatus: async (orderId: string, status: OrderStatus): Promise<{ status: string; new_status: string }> => {
    const response = await ApiClient.post(`/staff/ref-share/orders/${orderId}/change_status`, null, {
      params: { status }
    });
    return response.data;
  },

  /**
   * Get available models for filter
   */
  getAvailableModels: async (params?: { page?: number; page_size?: number; search?: string }): Promise<AvailableModelsResponse> => {
    const response = await ApiClient.get('/staff/ref-share/orders/models', { params });
    return response.data;
  },

  // ============ Costs (Bills) ============

  /**
   * Get list of RevShare costs (bills)
   */
  getCosts: async (params?: GetRefShareCostsParams): Promise<RefShareCostsResponse> => {
    const response = await ApiClient.get('/staff/ref-share/costs', { params });
    return response.data;
  },
};
